import { db as legacyDatabase, LEGACY_DATABASE_NAME, LeitourDatabase } from './db';

const USER_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const USER_DATABASE_PREFIX = 'leitour-user-';

export interface ActiveDatabaseSnapshot {
  databaseName: string;
  revision: number;
  userId: string | null;
}

export class InvalidDatabaseIdentityError extends Error {
  constructor() {
    super('A identidade recebida da conta não é um UUID válido. A biblioteca local não foi trocada.');
    this.name = 'InvalidDatabaseIdentityError';
  }
}

export class ActiveReadingSessionError extends Error {
  constructor() {
    super('Finalize ou descarte a sessão de leitura atual antes de entrar em outra conta ou sair.');
    this.name = 'ActiveReadingSessionError';
  }
}

export const normalizeDatabaseUserId = (userId: string): string => {
  const normalized = userId.trim().toLowerCase();
  if (!USER_ID_PATTERN.test(normalized)) throw new InvalidDatabaseIdentityError();
  return normalized;
};

export const resolveDatabaseName = (userId: string | null, legacyName = LEGACY_DATABASE_NAME): string =>
  userId === null ? legacyName : `${USER_DATABASE_PREFIX}${normalizeDatabaseUserId(userId)}`;

export class ActiveDatabaseManager {
  private activeDatabase: LeitourDatabase;
  private readonly databases = new Map<string, LeitourDatabase>();
  private readonly legacyName: string;
  private activeOperationCount = 0;
  private idleWaiters = new Set<() => void>();
  private listeners = new Set<() => void>();
  private snapshot: ActiveDatabaseSnapshot;
  private switchBarrier: Promise<void> | null = null;
  private switchQueue: Promise<void> = Promise.resolve();

  constructor(initialDatabase = legacyDatabase, legacyName = LEGACY_DATABASE_NAME) {
    this.activeDatabase = initialDatabase;
    this.legacyName = legacyName;
    this.databases.set(initialDatabase.name, initialDatabase);
    this.snapshot = { databaseName: initialDatabase.name, revision: 0, userId: null };
  }

  getActiveDatabase = (): LeitourDatabase => this.activeDatabase;

  getDatabaseNameForUser = (userId: string | null): string => resolveDatabaseName(userId, this.legacyName);

  getSnapshot = (): ActiveDatabaseSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  async runWithActiveDatabase<Result>(operation: (database: LeitourDatabase) => Promise<Result>): Promise<Result> {
    while (this.switchBarrier) await this.switchBarrier;
    const database = this.activeDatabase;
    this.activeOperationCount += 1;
    try {
      return await operation(database);
    } finally {
      this.activeOperationCount -= 1;
      if (this.activeOperationCount === 0) {
        for (const resolve of this.idleWaiters) resolve();
        this.idleWaiters.clear();
      }
    }
  }

  async assertCanLeaveActiveDatabase(): Promise<void> {
    const openSession = await this.runWithActiveDatabase(
      (database) => database.sessions.where('status').anyOf('active', 'paused').first()
    );
    if (openSession) throw new ActiveReadingSessionError();
  }

  async assertCanSwitchTo(userId: string | null): Promise<void> {
    const normalizedUserId = userId === null ? null : normalizeDatabaseUserId(userId);
    const targetName = this.getDatabaseNameForUser(normalizedUserId);
    if (targetName !== this.snapshot.databaseName) await this.assertCanLeaveActiveDatabase();
  }

  switchToUser(userId: string | null): Promise<void> {
    const normalizedUserId = userId === null ? null : normalizeDatabaseUserId(userId);
    const targetName = this.getDatabaseNameForUser(normalizedUserId);
    const operation = this.switchQueue.then(() => this.performSwitch(targetName, normalizedUserId));
    this.switchQueue = operation.catch(() => undefined);
    return operation;
  }

  closeAll(): void {
    for (const database of this.databases.values()) database.close();
  }

  private async performSwitch(targetName: string, userId: string | null): Promise<void> {
    if (targetName === this.snapshot.databaseName) return;

    let releaseBarrier: () => void = () => undefined;
    const barrier = new Promise<void>((resolve) => { releaseBarrier = resolve; });
    this.switchBarrier = barrier;
    try {
      await this.waitForIdleOperations();
      const openSession = await this.activeDatabase.sessions.where('status').anyOf('active', 'paused').first();
      if (openSession) throw new ActiveReadingSessionError();

      const previousDatabase = this.activeDatabase;
      const nextDatabase = this.databases.get(targetName) ?? new LeitourDatabase(targetName);
      this.databases.set(targetName, nextDatabase);
      await nextDatabase.open();
      this.activeDatabase = nextDatabase;
      previousDatabase.close();
      this.snapshot = {
        databaseName: targetName,
        revision: this.snapshot.revision + 1,
        userId
      };
      for (const listener of this.listeners) listener();
    } catch (error) {
      if (error instanceof ActiveReadingSessionError) throw error;
      const targetDatabase = this.databases.get(targetName);
      targetDatabase?.close();
      throw new Error('Não foi possível abrir a biblioteca local desta conta. Seus outros bancos locais não foram alterados.', { cause: error });
    } finally {
      if (this.switchBarrier === barrier) this.switchBarrier = null;
      releaseBarrier();
    }
  }

  private waitForIdleOperations(): Promise<void> {
    if (this.activeOperationCount === 0) return Promise.resolve();
    return new Promise((resolve) => this.idleWaiters.add(resolve));
  }
}

export const databaseManager = new ActiveDatabaseManager();
